import React, { cloneElement } from "react";
import {
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  EditProps,
  AutocompleteArrayInput,
  ReferenceArrayInput,
  EditContextProvider,
  useEditController,
  Toolbar,
  SaveButton,
  Title,
} from "react-admin";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import { Grid, Typography } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import _ from "lodash";
import { labelStyle } from "utils/style";

const AssignmentEditForm = (props: any) => {
  const controllerProps = useEditController(props);
  const {
    basePath, // deduced from the location, useful for action buttons
    // defaultTitle, // the translated title based on the resource, e.g. 'Post #123'
    loaded, // boolean that is false until the record is available
    // loading, // boolean that is true on mount, and false once the record was fetched
    record, // record fetched via dataProvider.getOne() based on the id from the location
    redirect, // the default redirection route. Defaults to 'list'
    resource, // the resource name, deduced from the location. e.g. 'posts'
    save, // the update callback, to be passed to the underlying form as submit handler
    saving, // boolean that becomes true when the dataProvider is called to update the record
    version, // integer used by the refresh feature
  } = controllerProps;

  const formatRecord = (res: any) => {
    if (res) {
      if (res.backupDrivers) {
        const backupDriverClone = _.cloneDeep(res.backupDrivers);
        const backupDriversIds = backupDriverClone.map((item: any) => {
          return item.id;
        });
        res.backupDriverIds = backupDriversIds;
      }

      if (res.backupVehicles) {
        const backupVehicleClone = _.cloneDeep(res.backupVehicles);
        const backupVehiclesIds = backupVehicleClone.map((item: any) => {
          return item.id;
        });
        res.backupVehicleIds = backupVehiclesIds;
      }

      if (res.backupBusAssistants) {
        const backupBusAssistantClone = _.cloneDeep(res.backupBusAssistants);
        const backupBusAssistantsIds = backupBusAssistantClone.map(
          (item: any) => {
            return item.id;
          }
        );
        res.backupBusAssistantIds = backupBusAssistantsIds;
      }
    }
    return res;
  };

  return (
    <EditContextProvider value={controllerProps}>
      <div>
        <Typography
          variant="h4"
          style={labelStyle}
        >{`Bus Services #${record?.id}`}</Typography>

        <Title title={`Bus Services #${record?.id}`} />

        {loaded &&
          cloneElement(props.children, {
            className: "MuiPaper-root MuiPaper-elevation1",
            basePath,
            record: formatRecord(record),
            redirect,
            resource,
            save,
            saving,
            version,
          })}
      </div>
    </EditContextProvider>
  );
};

const AssignmentEdit = (props: EditProps) => {
  const { id } = props;

  const breadcrumb = [
    {
      title: "Bus Services",
      link: `/assignments`,
    },
    {
      title: id,
      link: ``,
    },
  ];

  const AssignmentEditToolbar = (props: any) => (
    <Toolbar {...props}>
      <SaveButton redirect="show" label="Assign" />
    </Toolbar>
  );

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <AssignmentEditForm {...props} undoable={false}>
        <SimpleForm toolbar={<AssignmentEditToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Main Operations</Typography>
            </Grid>
            <Grid item xs={4}>
              <ReferenceInput
                label="Select Driver"
                source="driverId"
                reference="drivers"
                filterToQuery={(searchText: any) => {
                  if (searchText !== "") return { search: searchText };
                }}
                allowEmpty
              >
                <AutocompleteInput optionText="fullName" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={4}>
              <ReferenceInput
                label="Select Bus Assistant"
                source="busAssistantId"
                reference="busAssistants"
                filterToQuery={(searchText: any) => {
                  if (searchText !== "") return { search: searchText };
                }}
                allowEmpty
              >
                <AutocompleteInput optionText="fullName" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="Select Vehicle"
                source="vehicleId"
                reference="vehicles"
                filterToQuery={(searchText: any) => {
                  if (searchText !== "") return { search: searchText };
                }}
                allowEmpty
              >
                <AutocompleteInput optionText="plateNumber" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Backup</Typography>
            </Grid>

            <Grid item xs={4}>
              <ReferenceArrayInput
                label="Select Backup Drivers"
                source="backupDriverIds"
                reference="drivers"
                filterToQuery={(searchText: any) => {
                  if (searchText !== "") return { search: searchText };
                }}
              >
                <AutocompleteArrayInput optionText="fullName" />
              </ReferenceArrayInput>
            </Grid>
            <Grid item xs={4}>
              <ReferenceArrayInput
                label="Select Backup Bus Assistants"
                source="backupBusAssistantIds"
                reference="busAssistants"
                filterToQuery={(searchText: any) => {
                  if (searchText !== "") return { search: searchText };
                }}
              >
                <AutocompleteArrayInput optionText="fullName" />
              </ReferenceArrayInput>
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={4}>
              <ReferenceArrayInput
                label="Select Backup Vehicles"
                source="backupVehicleIds"
                reference="vehicles"
                filterToQuery={(searchText: any) => {
                  if (searchText !== "") return { search: searchText };
                }}
              >
                <AutocompleteArrayInput optionText="plateNumber" />
              </ReferenceArrayInput>
            </Grid>
            <Grid item xs={8}></Grid>
          </SanitizedGrid>
        </SimpleForm>
      </AssignmentEditForm>
    </>
  );
};

export default AssignmentEdit;
